// v1.4.6 (Admin Add-to-Cart Protection with Modal)

import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Spinner, Button, Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SearchBar from '../components/SearchBar';
import { useCart } from '../context/CartContext';

const ITEMS_PER_PAGE = 8;

function Products() {
  const { addToCart, cart } = useCart();
  const safeCart = Array.isArray(cart) ? cart : [];

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [products, setProducts] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [page, setPage] = useState(1);

  const [activeImage, setActiveImage] = useState({});

  /* ---------------- ADMIN MODAL ---------------- */
  const [showModal, setShowModal] = useState(false);

  const user = JSON.parse(localStorage.getItem('user'));

  const isAdmin =
    user?.role === 'admin' ||
    user?.email === 'admin' ||
    user?.email === 'testadmin';

  /* ---------------- FETCH PRODUCTS ---------------- */
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axios.get('https://apiclientportal.myidealtd.co.uk/api/products');
        setProducts(Array.isArray(res.data?.products) ? res.data.products : []);
      } catch (err) {
        console.error(err);
        setError('Failed to load products');
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  /* ---------------- CATEGORIES ---------------- */
  const categories = useMemo(() => {
    const cats = products.map(p => p.category).filter(Boolean);
    return ['All', ...new Set(cats)];
  }, [products]);

  /* ---------------- FILTER ---------------- */
  const filteredProducts = useMemo(() => {
    let list = [...products];

    if (selectedCategory !== 'All') {
      list = list.filter(p => p.category === selectedCategory);
    }

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter(p => p.name?.toLowerCase().includes(q));
    }

    return list;
  }, [products, selectedCategory, searchTerm]);

  /* ---------------- PAGINATION ---------------- */
  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  const paginatedProducts = filteredProducts.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  /* ---------------- ADD TO CART ---------------- */
  const handleAddToCart = product => {
    if (isAdmin) {
      setShowModal(true);
      return;
    }

    if (safeCart.some(item => item._id === product._id)) return;

    addToCart(product);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div>

      {/* ================= ADMIN BLOCK MODAL ================= */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Access Restricted</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          🚫 Admin users are not allowed to add items to the cart.
          <br /><br />
          Please log in as a customer account.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="dark" onClick={() => setShowModal(false)}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ================= HERO ================= */}
      <div
        style={{
          background: 'linear-gradient(135deg, #f9fafb, #eef2f7)',
          padding: '80px 20px',
          textAlign: 'center'
        }}
      >
        <h1 style={{ fontSize: 42, fontWeight: 800 }}>
          Your Products, Online 24/7
        </h1>
        <p style={{ fontSize: 18, opacity: 0.85, maxWidth: 600, margin: '16px auto' }}>
          Reach customers anytime, anywhere with a modern shopping experience.
        </p>
        <Button size="lg" variant="dark" style={{ borderRadius: 30 }}>
          Shop Now
        </Button>
      </div>

      {/* ================= CATEGORIES ================= */}
      <div className="container py-5">
        <Row className="g-3 justify-content-center">
          {categories.map(cat => (
            <Col key={cat} xs={6} md={3} lg={2}>
              <div
                onClick={() => { setSelectedCategory(cat); setPage(1); }}
                style={{
                  cursor: 'pointer',
                  background: selectedCategory === cat ? '#0d6efd' : '#f8f9fa',
                  color: selectedCategory === cat ? '#fff' : '#212529',
                  padding: 16,
                  borderRadius: 14,
                  textAlign: 'center',
                  fontWeight: 600,
                  boxShadow: '0 6px 16px rgba(0,0,0,0.08)'
                }}
              >
                {cat}
              </div>
            </Col>
          ))}
        </Row>
      </div>

      {/* ================= SEARCH ================= */}
      <div className="container mb-4">
        <SearchBar
          value={searchTerm}
          onChange={value => { setSearchTerm(value); setPage(1); }}
        />
      </div>

      {/* ================= PRODUCTS ================= */}
      <div className="container pb-5">
        {error && <p className="text-danger text-center">{error}</p>}

        <Row className="g-4">
          {paginatedProducts.map(product => {
            const isInCart = safeCart.some(i => i._id === product._id);
            const images = product.images || [];
            const mainImage = activeImage[product._id] || images[0];

            return (
              <Col key={product._id} xs={6} md={4} lg={3}>
                <Card className="h-100 border-0" style={{ borderRadius: 18 }}>
                  
                  <Link to={`/product/${product._id}`}>
                    <div style={{
                      height: 220,
                      background: '#f8f9fa',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {mainImage ? (
                        <img
                          src={`https://apiclientportal.myidealtd.co.uk/uploads/${mainImage}`}
                          alt={product.name}
                          style={{ maxHeight: 200, objectFit: 'contain' }}
                        />
                      ) : <span>No Image</span>}
                    </div>
                  </Link>

                  <Card.Body className="text-center d-flex flex-column">
                    <Card.Title style={{ fontSize: 14 }}>
                      {product.name}
                    </Card.Title>

                    <div style={{ fontWeight: 700 }}>
                      £{product.price.toFixed(2)}
                    </div>

                    <Button
                      size="sm"
                      variant={isInCart ? 'success' : 'primary'}
                      disabled={isInCart}
                      onClick={() => handleAddToCart(product)}
                      style={{ marginTop: 'auto' }}
                    >
                      {isInCart ? 'In Cart' : 'Add to Cart'}
                    </Button>
                  </Card.Body>

                </Card>
              </Col>
            );
          })}
        </Row>
      </div>
    </div>
  );
}

export default Products;